import { omit } from 'lodash-es';

import { appVersion } from 'utils/app-version-info';
import { getConfigValue, isWeb } from 'utils/environment';

import { startDatadogTrace } from './datadog-trace';
import { PerformanceTrace, TraceName } from './types';
export { TraceName } from './types';

const MAX_TRACE_COUNT = 250;
const MAX_TRACE_METADATA_COUNT = 5;

interface TraceEntry extends PerformanceTrace {
  startedAt: number;
  metadata: Record<string, string>;
}

const traces = new Map<string, TraceEntry>();

const ALLOW_DEV_ONLY_DEBUG_TRACES = !getConfigValue({ key: 'disableDevTraceLogging' }) && __DEV__;

const sanitizeTraceMetadata = (
  ...metadataSources: Record<string, string>[]
): Record<string, string> => {
  const metadata = metadataSources.reduce<Record<string, string>>((result, source) => {
    for (const [key, value] of Object.entries(source)) {
      if (key && typeof value === 'string' && value) {
        result[key] = value;
      }
    }
    return result;
  }, {});

  return Object.fromEntries(Object.entries(metadata).slice(0, MAX_TRACE_METADATA_COUNT));
};

/**
 * Start a trace with a string name. Tracing failures are intentionally isolated from app flows.
 * The returned handle can be stopped directly, or the trace can be stopped by name with stopTrace.
 */
export const startTrace = (
  traceName: string | TraceName,
  additionalMetadata: Record<string, string> = {}
): PerformanceTrace => {
  const startedAt = Date.now();
  const metadata = sanitizeTraceMetadata({ version: appVersion }, additionalMetadata);
  const providerTrace = startDatadogTrace(traceName, metadata, startedAt);

  let stopped = false;
  const traceEntry = {} as TraceEntry;
  Object.assign(traceEntry, {
    startedAt,
    metadata,
    stop: async (stopMetadata: Record<string, string> = {}) => {
      if (stopped) {
        return;
      }
      stopped = true;

      if (traces.get(traceName) === traceEntry) {
        traces.delete(traceName);
      }

      const mergedMetadata = sanitizeTraceMetadata(metadata, stopMetadata);

      if (ALLOW_DEV_ONLY_DEBUG_TRACES) {
        DEV_ONLY_colorLog(traceName, startedAt, mergedMetadata);
      }

      await providerTrace.stop(mergedMetadata);
    },
  });

  if (traces.size >= MAX_TRACE_COUNT) {
    const oldestKey = traces.keys().next().value;
    if (oldestKey) {
      traces.delete(oldestKey);
    }
  }
  traces.set(traceName, traceEntry);
  return traceEntry;
};

/**
 * Stop a trace by name. Safe to call even if the trace does not exist.
 */
export const stopTrace = async (
  traceName: string | TraceName,
  additionalMetadata: Record<string, string> = {}
) => {
  const traceEntry = traces.get(traceName);
  if (!traceEntry) {
    return null;
  }

  await traceEntry.stop(additionalMetadata);
  return undefined;
};

const devOnlyDupeTracker: string[] = [];

// In __DEV__ only, log colors on a gradient based on duration and flag recent duplicates.
function DEV_ONLY_colorLog(traceName: string, startedAt: number, metadata: Record<string, string>) {
  const timeSeconds = (Date.now() - startedAt) / 1000;
  devOnlyDupeTracker.push(traceName);
  let dupeAlert = '';
  const arrayCount = devOnlyDupeTracker.filter(x => x === traceName).length;
  if (arrayCount > 1) {
    dupeAlert = ` \x1b[37m ⚠️ (x${arrayCount} traces recently!) \x1b[0m`;
  }
  setTimeout(() => {
    const index = devOnlyDupeTracker.indexOf(traceName);
    if (index >= 0) {
      devOnlyDupeTracker.splice(index, 1);
    }
  }, 5000);

  const clampedValue = Math.max(0, Math.min(2, timeSeconds));
  const serializedMetadata = JSON.stringify(omit(metadata, ['userId', 'version']));
  const message = `TRACE END - ${timeSeconds.toFixed(
    2
  )}s - ${traceName}${dupeAlert} ${new Date().toISOString()} ${
    serializedMetadata === '{}' ? '' : serializedMetadata
  }`;

  if (isWeb) {
    let style = '';
    if (clampedValue <= 1) {
      const red = Math.round(clampedValue * 255);
      style = `color: rgb(${red}, 255, 0);`;
    } else {
      const green = 255 - Math.round((clampedValue - 1) * 255);
      style = `color: rgb(255, ${green}, 0);`;
    }
    // eslint-disable-next-line no-console
    console.log(`%c${message}`, style);
  } else {
    let colorCode: number;
    if (clampedValue <= 1) {
      const redScaled = Math.round(clampedValue * 5);
      colorCode = 16 + 36 * redScaled + 6 * 5;
    } else {
      const greenScaled = 5 - Math.round((clampedValue - 1) * 5);
      colorCode = 16 + 36 * 5 + 6 * greenScaled;
    }
    // eslint-disable-next-line no-console
    console.log(`\x1b[38;5;${colorCode}m${message}\x1b[0m`);
  }
}
